/**
 * LandingPage — Public landing page
 *
 * Accessible to all visitors (authenticated or not).
 * Features: hero, features section, CTA, and footer with links to terms/privacy.
 * Styled with MUI sx — customizable via theme colors.
 */
import { useContext } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import { AuthContext } from "context/AuthContext";
import { useI18n, useT } from "context/I18nContext";
import kadminConfig from "kadmin.config";

import kadminWordmark from "assets/images/kadmin-wordmark.svg";

// ---------------------------------------------------------------------------
// Palette constants (derived from theme, with fallbacks)
// ---------------------------------------------------------------------------
const PRIMARY = "#0D9488";
const TEAL = "#14B8A6";
const DARK_BG = "#0F1114";
const DARK_SURFACE = "#161B1F";

// ---------------------------------------------------------------------------
// Feature data
// ---------------------------------------------------------------------------
const featureKeys = [
  { icon: "shield", titleKey: "landing.features.rbac.title", descKey: "landing.features.rbac.description" },
  { icon: "translate", titleKey: "landing.features.i18n.title", descKey: "landing.features.i18n.description" },
  { icon: "palette", titleKey: "landing.features.theme.title", descKey: "landing.features.theme.description" },
  { icon: "speed", titleKey: "landing.features.production.title", descKey: "landing.features.production.description" },
];

// ---------------------------------------------------------------------------
// Reusable styled components
// ---------------------------------------------------------------------------
const GlowButton = ({ children, ...props }) => (
  <Button
    variant="contained"
    {...props}
    sx={{
      background: `linear-gradient(135deg, ${PRIMARY} 0%, #14B8A6 100%)`,
      color: "#fff",
      fontWeight: 600,
      px: 4,
      py: 1.5,
      borderRadius: "12px",
      textTransform: "none",
      fontSize: "1rem",
      boxShadow: `0 4px 24px ${PRIMARY}44`,
      "&:hover": {
        background: `linear-gradient(135deg, #0F766E 0%, #7C3AED 100%)`,
        boxShadow: `0 6px 32px ${PRIMARY}66`,
        transform: "translateY(-1px)",
      },
      transition: "all 0.2s ease",
      ...props.sx,
    }}
  >
    {children}
  </Button>
);

const OutlineButton = ({ children, ...props }) => (
  <Button
    variant="outlined"
    {...props}
    sx={{
      borderColor: "rgba(255,255,255,0.3)",
      color: "#fff",
      fontWeight: 500,
      px: 4,
      py: 1.5,
      borderRadius: "12px",
      textTransform: "none",
      fontSize: "1rem",
      "&:hover": {
        borderColor: "#fff",
        background: "rgba(255,255,255,0.05)",
      },
      ...props.sx,
    }}
  >
    {children}
  </Button>
);

// ---------------------------------------------------------------------------
// Feature Card
// ---------------------------------------------------------------------------
const FeatureCard = ({ icon, title, description }) => (
  <Box
    sx={{
      background: "rgba(255,255,255,0.03)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "16px",
      p: 3.5,
      height: "100%",
      transition: "all 0.3s ease",
      "&:hover": {
        transform: "translateY(-4px)",
        border: `1px solid ${PRIMARY}44`,
        boxShadow: `0 8px 32px ${PRIMARY}22`,
      },
    }}
  >
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: "12px",
        background: `linear-gradient(135deg, ${PRIMARY}22, ${TEAL}22)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 2,
      }}
    >
      <Icon sx={{ color: PRIMARY, fontSize: 24 }}>{icon}</Icon>
    </Box>
    <Typography
      variant="h6"
      sx={{ color: "#F1F5F9", fontWeight: 600, mb: 1, fontSize: "1.1rem" }}
    >
      {title}
    </Typography>
    <Typography sx={{ color: "#94A3B8", fontSize: "0.9rem", lineHeight: 1.6 }}>
      {description}
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Landing Page Component
// ---------------------------------------------------------------------------
function LandingPage() {
  const navigate = useNavigate();
  const { currentUser, activeRole } = useContext(AuthContext);
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));
  const t = useT();
  const { locale, setLocale, supportedLocales } = useI18n();

  const brandName = kadminConfig.name || "Kadmin";

  const LOCALE_LABELS = { en: "EN", fr: "FR" };
  const nextLocale = supportedLocales.find((l) => l !== locale) || supportedLocales[0];

  const handleGetStarted = () => {
    if (currentUser && activeRole) {
      navigate(`/${activeRole}/dashboard`);
    } else {
      navigate("/login");
    }
  };

  return (
    <Box sx={{ background: DARK_BG, minHeight: "100vh", overflow: "hidden" }}>
      {/* ── Header ─────────────────────────────────────────────────── */}
      <Box
        component="header"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          backdropFilter: "blur(20px)",
          background: "rgba(15, 15, 26, 0.8)",
          borderBottom: "1px solid rgba(255,255,255,0.06)",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
          }}
        >
          <Box
            component="img"
            src={kadminWordmark}
            alt={brandName}
            sx={{ height: 32, display: "block", filter: "brightness(0) invert(1)" }}
          />

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <Button
              size="small"
              onClick={() => setLocale(nextLocale)}
              sx={{
                color: "#94A3B8",
                fontWeight: 600,
                fontSize: "0.8rem",
                minWidth: "auto",
                px: 1.5,
                textTransform: "none",
                "&:hover": { color: "#fff" },
              }}
            >
              {LOCALE_LABELS[nextLocale] || nextLocale.toUpperCase()}
            </Button>
            {currentUser ? (
              <GlowButton onClick={() => navigate(`/${activeRole}/dashboard`)} sx={{ py: 1, px: 3, minWidth: 120 }}>
                {t("landing.header.dashboard")}
              </GlowButton>
            ) : (
              <>
                <OutlineButton onClick={() => navigate("/login")} sx={{ py: 1, px: 3, minWidth: 120 }}>
                  {t("landing.header.signIn")}
                </OutlineButton>
                <GlowButton onClick={() => navigate("/login")} sx={{ py: 1, px: 3, minWidth: 120 }}>
                  {t("landing.header.getStarted")}
                </GlowButton>
              </>
            )}
          </Box>
        </Container>
      </Box>

      {/* ── Hero ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          pt: { xs: 16, md: 20 },
          pb: { xs: 8, md: 12 },
          textAlign: "center",
          position: "relative",
        }}
      >
        {/* Decorative gradient orbs */}
        <Box
          sx={{
            position: "absolute",
            top: "10%",
            left: "20%",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${PRIMARY}15 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <Box
          sx={{
            position: "absolute",
            bottom: "10%",
            right: "15%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${TEAL}12 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />

        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: "2.5rem", md: "3.75rem" },
              fontWeight: 800,
              lineHeight: 1.15,
              mb: 2.5,
              color: "#F1F5F9",
            }}
          >
            {t("landing.hero.title")}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              color: "#94A3B8",
              maxWidth: 600,
              mx: "auto",
              mb: 5,
              lineHeight: 1.7,
            }}
          >
            {t("landing.hero.subtitle")}
          </Typography>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              flexDirection: isMobile ? "column" : "row",
              alignItems: "center",
            }}
          >
            <GlowButton onClick={handleGetStarted} sx={{ minWidth: 200 }}>
              {currentUser ? t("landing.hero.ctaLoggedIn") : t("landing.hero.cta")}
            </GlowButton>
            <OutlineButton
              onClick={() => {
                document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
              }}
              sx={{ minWidth: 200 }}
            >
              {t("landing.hero.seeFeatures")}
            </OutlineButton>
          </Box>
        </Container>
      </Box>

      {/* ── Features ───────────────────────────────────────────────── */}
      <Box id="features" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              color: "#F1F5F9",
              mb: 1.5,
            }}
          >
            {t("landing.features.title")}
          </Typography>
          <Typography
            sx={{
              textAlign: "center",
              color: "#94A3B8",
              fontSize: "1.05rem",
              maxWidth: 550,
              mx: "auto",
              mb: 6,
            }}
          >
            {t("landing.features.subtitle")}
          </Typography>

          <Grid container spacing={3}>
            {featureKeys.map((f) => (
              <Grid item xs={12} sm={6} md={3} key={f.titleKey}>
                <FeatureCard icon={f.icon} title={t(f.titleKey)} description={t(f.descKey)} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── CTA ────────────────────────────────────────────────────── */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="sm" sx={{ textAlign: "center" }}>
          <Box
            sx={{
              background: `linear-gradient(135deg, ${DARK_SURFACE}, ${PRIMARY}11)`,
              border: `1px solid ${PRIMARY}33`,
              borderRadius: "24px",
              p: { xs: 4, md: 6 },
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: "#F1F5F9", mb: 2, fontSize: { xs: "1.5rem", md: "1.75rem" } }}
            >
              {t("landing.cta.title")}
            </Typography>
            <Typography sx={{ color: "#94A3B8", mb: 4, fontSize: "1rem" }}>
              {t("landing.cta.subtitle")}
            </Typography>
            <GlowButton onClick={handleGetStarted}>
              {currentUser ? t("landing.cta.buttonLoggedIn") : t("landing.cta.button")}
            </GlowButton>
          </Box>
        </Container>
      </Box>

      {/* ── Footer ─────────────────────────────────────────────────── */}
      <Box
        component="footer"
        sx={{
          borderTop: "1px solid rgba(255,255,255,0.06)",
          py: 4,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Typography sx={{ color: "#64748B", fontSize: "0.85rem" }}>
            {t("landing.footer.rights", { year: new Date().getFullYear() })}
          </Typography>
          <Box sx={{ display: "flex", gap: 3 }}>
            <Typography
              component="a"
              href="/terms"
              sx={{
                color: "#94A3B8",
                fontSize: "0.85rem",
                textDecoration: "none",
                "&:hover": { color: PRIMARY },
              }}
            >
              {t("landing.footer.terms")}
            </Typography>
            <Typography
              component="a"
              href="/privacy"
              sx={{
                color: "#94A3B8",
                fontSize: "0.85rem",
                textDecoration: "none",
                "&:hover": { color: PRIMARY },
              }}
            >
              {t("landing.footer.privacy")}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
